import { AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Renderer2,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapMarker, MapPolygon, MapPolyline } from '@angular/google-maps';
import { PopupRef, PopupService } from '@progress/kendo-angular-popup';
import { QPointIconModule } from '@qpoint/icons';
import { isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, merge, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, startWith, take, takeUntil, tap, throttleTime } from 'rxjs/operators';

import { AfterViewInitDirective } from '../../directives/after-view-init-trigger.directive';
import { MapMarkerInfoWindowService } from '../../services/map-marker-info-window.service';
import { QPointMapsLoader } from '../../services/maps-loader.service';
import { CustomPopup, CustomPopupPosition } from './customPopup';
import { IFocusElement } from './focus-element';
import { HasPosition } from './has-position';
import { InternalMapService } from './internal-map.service';
import { MapCircleComponent } from './map-circle/map-circle.component';
import {
  FunctionType,
  MapContextMenuComponent,
  MapContextMenuEntry,
  TargetComponent,
} from './map-context-menu/map-context-menu.component';
import { MapDirectionComponent } from './map-direction/map-direction.component';
import { MapFooterOverlayDirective } from './map-footer-overlay.directive';
import { getMapLocalStorage, saveMapLocalStorage } from './map-helpers';
import { MapInfoWindowComponent } from './map-info-window/map-info-window.component';
import { MapMarkerComponent } from './map-marker/map-marker.component';
import { MarkerLabelInternalDirective } from './map-marker/marker-label-internal.directive';
import { MapModeSelectionComponent } from './map-mode-selection/map-mode-selection.component';
import { MapModeStateService } from './map-mode-selection/map-mode-state.service';
import { QPointMapModeConfiguration } from './map-mode-selection/mode';
import { MapPolygonStateService } from './map-polygon/map-polygon-state.service';
import { MapPolygonComponent } from './map-polygon/map-polygon.component';
import { MapPolygonsComponent } from './map-polygons/map-polygons.component';
import { MapPolylineStateService } from './map-polyline/map-polyline-state.service';
import { MapPolylineComponent } from './map-polyline/map-polyline.component';
import { MapTypeControlComponent } from './map-type-control/map-type-control.component';
import {
  MapZoomFitToBoundsControlsComponent,
} from './map-zoom-fitToBounds-controls/map-zoom-fitToBounds-controls.component';
import { mapStyles } from './map.styles';
import { GeoJsonToGmapsPipe } from './pipes/geo-json-to-gmaps.pipe';

import LatLng = google.maps.LatLng;
import LatLngLiteral = google.maps.LatLngLiteral;
export * from './default-map-icons';

export interface ILatLng {
  lat: number;
  lng: number;
}

export interface CustomPopupReference {
  reference: HasPosition;
  popup: CustomPopup;
}

export enum FocusEnum {
  Site = 'site',
  Overview = 'overview',
  Plant = 'plant',
}

type ContextMenuTarget = 'Polyline' | 'Polygon';

@Component({
    selector: 'qpoint-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [NgIf, GoogleMapsModule, NgClass, NgFor, AfterViewInitDirective, QPointIconModule, NgTemplateOutlet, MarkerLabelInternalDirective, AsyncPipe, GeoJsonToGmapsPipe],
    providers: [InternalMapService, MapPolygonStateService, MapPolylineStateService, MapModeStateService, MapMarkerInfoWindowService]
})
export class MapComponent implements OnInit, OnChanges, OnDestroy, AfterContentInit, AfterViewInit {
  public mapInitialLoaded = false;
  onChanges = new Subject<SimpleChanges>();
  zoomChanges = new Subject<number>();
  triggerContextMenu$ = new Subject<{ event: MouseEvent | PointerEvent, anchor?: HTMLElement | ElementRef, target?: ContextMenuTarget }>();
  longClickTimer: Subscription;
  private mapIdleTriggeredAction: Subject<void> = new Subject<void>();
  private stopFitToBounds: Subject<void> = new Subject();
  private zoomChangedByComponent$: Subject<void> = new Subject<void>();
  private appendPopupsThrottleSubject$: Subject<void> = new Subject<void>();
  private appendPopupsDelay = 500;
  private zoomChangedByComponent = false;
  private popupRef: PopupRef | null = null;
  private lastRightclickTarget = TargetComponent.Map;
  private polygonListener: google.maps.MapsEventListener[] = [];
  private polylineListener: google.maps.MapsEventListener[] = [];
  private customPopups: CustomPopupReference[] = [];
  private mapTypeControlRef: MapTypeControlComponent;
  private zoomControlsRef: MapZoomFitToBoundsControlsComponent;
  private mapMarkerInfoWindowService = inject(MapMarkerInfoWindowService);

  constructor(
    public mapsLoaderService: QPointMapsLoader,
    private mapPolygonStateService: MapPolygonStateService,
    private mapPolylineStateService: MapPolylineStateService,
    @Optional() private popupService: PopupService, // not used in maps on Android/iOS apps
    private cdr: ChangeDetectorRef,
    public mapModeState: MapModeStateService,
    public viewContainerRef: ViewContainerRef,
    private injector: Injector,
    private renderer: Renderer2
  ) {
    toObservable(mapModeState.modesSignal)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(modes => {
        this.mapModeChanged.emit(modes);
      });
  }

  public googleMapOptions: google.maps.MapOptions;
  public markers = signal<MapMarkerComponent[]>([]);
  public markers$ = toObservable(this.markers);
  public circles: MapCircleComponent[] = [];
  public infoWindows: MapInfoWindowComponent[] = [];
  polygons: WritableSignal<MapPolygonComponent[]> = signal([]);
  multiPolygons: WritableSignal<MapPolygonsComponent[]> = signal([]);
  polylines: WritableSignal<MapPolylineComponent[]> = signal([]);
  directions: WritableSignal<MapDirectionComponent[]> = signal([]);
  openMarkerInfoWindows = this.mapMarkerInfoWindowService.openMarkerInfoWindows;
  closeMapTypeSelectionPopup = new BehaviorSubject<boolean>(null);
  closeZoomTypeSelectionPopup = new BehaviorSubject<boolean>(null);

  private ngUnsubscribe = new Subject<void>();
  private fitToBoundsThrottleSubject = new Subject<boolean>();
  private previousBounds: google.maps.LatLngBounds;

  @ViewChild(GoogleMap)
  public map: GoogleMap;
  @ViewChildren('divsToShowPopups', { read: ElementRef })
  public divsToShowPopups: QueryList<ElementRef>;
  @ViewChildren(MarkerLabelInternalDirective)
  public markerLabelsToShow: QueryList<MarkerLabelInternalDirective>;
  @ViewChildren(MapMarker)
  public googleMapMarkers: QueryList<MapMarker>;
  @ContentChildren(MapMarkerComponent)
  public markerQueryList: QueryList<MapMarkerComponent>;
  @ViewChildren(MapInfoWindow)
  public googleInfoWindows: QueryList<MapInfoWindow>;
  @ContentChildren(MapInfoWindowComponent)
  public infoWindowsQueryList: QueryList<MapInfoWindowComponent>;

  @ContentChildren(MapPolylineComponent) set mapPolylineComponents(mapPolylineComponents: QueryList<MapPolylineComponent>) {
    this.polylines.set(mapPolylineComponents.toArray());
  }

  @ContentChildren(MapCircleComponent)
  public circleQueryList: QueryList<MapCircleComponent>;

  @ContentChildren(MapPolygonComponent) set mapPolygonComponents(mapPolygonComponents: QueryList<MapPolygonComponent>) {
    this.polygons.set(mapPolygonComponents.toArray());
  }

  @ContentChildren(MapPolygonsComponent) set mapPolygonsComponents(mapPolygonsComponents: QueryList<MapPolygonsComponent>) {
    this.multiPolygons.set(mapPolygonsComponents.toArray());
  }

  @ContentChildren(MapDirectionComponent) set mapDirectionComponents(mapDirectionComponents: QueryList<MapDirectionComponent>) {
    this.directions.set(mapDirectionComponents.toArray());
  }

  @ViewChildren(MapPolygon) set gmapsPolygons(gmapsPolygons: QueryList<MapPolygon>) {
    this.setupPolygonEditListener(gmapsPolygons.toArray());
  }

  @ViewChildren(MapPolyline) set gmapsPolylines(gmapsPolylines: QueryList<MapPolyline>) {
    this.setupPolylineEditListener(gmapsPolylines.toArray());
  }

  @ContentChild(MapFooterOverlayDirective, { read: TemplateRef })
  public mapFooterOverlay: TemplateRef<any>;

  /**
   * Set to true to hide the map during the initial loading of markers, to prevent the map from jumping around. Has to be set to false after all data is ready to show the map. Default is false.
   */
  @Input()
  public initialLoading = false;
  @Input()
  public width: string | number | null;
  @Input()
  public zoom: number;
  @Input()
  public maxZoom: number;
  @Input()
  public minZoom: number = 5;
  @Input()
  public minZoomToShowLabels: number = 5;
  @Input()
  public height: string | number | null;
  @Input()
  public center: google.maps.LatLng | ILatLng;
  @Input()
  public zoomPosition: google.maps.ControlPosition;
  @Input()
  public mapTypePosition: google.maps.ControlPosition;
  @Input()
  public closeAllWindowsOnMarkerChange = true;
  @Input()
  public closeOtherWindowsOnMarkerKlick = true;

  /**
   * If set to true, the map will zoom to the level set in the zoom input when the map fits to bounds and a single poi is set, or the center and no poi is set. Default is true.
   * When multiple pois are set, this option is ignored.
   */
  @Input()
  public setZoomLevelOnFitBounds = true;
  @Input()
  public stickToCenter = false;
  @Input()
  public fitToBoundsDelay = 200;
  @Input()
  public gestureHandling: google.maps.MapOptions['gestureHandling'] = 'cooperative';
  @Input()
  public disableDefaultUi = false;
  /**
   * If set to true, the map will fit to bounds when markers are added, removed, or moved. Default is true.
   */
  @Input()
  public fitToBoundsOnMarkerChanges = true;
  @Input()
  public enableFullscreenControl = false;
  /**
   * If set to true, the map will stop fitting to bounds when the user interacts with the map, i.e., moves the map, or changes the zoom factor manually.
   */
  @Input() public stopFitToBoundsOnManualInteraction;
  @Input() public debugOutput = false;
  @Input() public contextMenu: MapContextMenuEntry[] = [];
  @Input() public contextMenuDisabled: Function;
  @Input() public showZoomButtons = true;
  @Input() public showFitBoundsButton = false;
  @Input() public focusButtonToolTipPath = 'common.map.focus';

  public _showFocusButton = signal<boolean>(false);
  @Input() set showFocusButton(value: boolean) {
    this._showFocusButton.set(value);
  }

  public _focusElements = signal<IFocusElement[]>([]);
  @Input() set focusElements(value: IFocusElement[]) {
    this._focusElements.set(value);
    if (this.zoomControlsRef) {
      this.zoomControlsRef.focusElements = value;
    }
  }

  @Input() public activeFocus: IFocusElement;
  @Input() public zoomInLabelPath = 'common.map.zoomIn';
  @Input() public zoomOutLabelPath = 'common.map.zoomOut';
  @Input() public fitBoundsLabelPath = 'common.map.fitBounds';
  @Input() public mapTypePath = 'common.map.mapType';
  @Input() public roadmapPath = 'common.map.roadmap';
  @Input() public hybridPath = 'common.map.hybrid';
  @Input() public overviewPath = 'common.map.overview';
  @Input() public locationPath = 'common.map.location';
  public _localStorageKey = signal<string>(null);
  @Input()
  set localStorageKey(value: string) {
    this._localStorageKey.set(value + '-qpoint-map');
  }

  public _activeMapType = signal<string>(null);
  @Input()
  set activeMapType(value: string) {
    this._activeMapType.set(value);
    if (this.map?.googleMap) {
      this.map.googleMap.setMapTypeId(this._activeMapType());
    }
  }

  public _activeFitBounds = signal<string>(null);
  @Input()
  set activeFitBounds(value: string) {
    this._activeFitBounds.set(value);
    if (this.mapTypeControlRef) {
      this.zoomControlsRef.activeFitBounds = value;
    }
  }

  @Input('modes') set modesSetter(modes: QPointMapModeConfiguration | QPointMapModeConfiguration[]) {
    this.mapModeState.setModes([].concat(modes));
  }

  @Output() public mapModeChanged = new EventEmitter<QPointMapModeConfiguration[]>();

  @Input() selectorTemplate: TemplateRef<any>;

  @Input() showSelector = false;

  @Input() handleEscapePress = true;

  @Output() public mapClick = new EventEmitter<google.maps.MapMouseEvent>();
  @Output() public mapRightClick = new EventEmitter<google.maps.MapMouseEvent>();
  @Output() public mapCenterChanged = new EventEmitter<void>();
  @Output() public mapLoaded = new EventEmitter<google.maps.Map>();
  @Output() polygonClick = new EventEmitter<google.maps.PolyMouseEvent>();
  @Output() mapTypeChanged = new EventEmitter<string>();
  @Output() fitBoundsChanged = new EventEmitter<string>();
  @Output() focusChanged = new EventEmitter<IFocusElement>();
  @Output() zoomChanged = new EventEmitter<number>();

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent) {
    if (this.handleEscapePress) {
      this.mapModeState.deactivateAll();
    } else {
      event.preventDefault();
    }
  }

  @HostListener('document:click', ['$event'])
  public documentClick(event: KeyboardEvent): void {
    if (this.popupRef && !this.popupRef.popupElement.contains(event.target as Node)) {
      this.checkAndClosePopupRef();
    }
  }

  public showMap = false;

  get isFullscreen(): boolean {
    return this.map?.googleMap?.getDiv()?.firstChild === document.fullscreenElement;
  }

  public setCenter(latLng: ILatLng, isHandledAsManualInput = false) {
    this.map.googleMap.setCenter(latLng);
    if (isHandledAsManualInput) {
      this.stopFitToBounds.next();
    }
  }

  private allElementsVisibleOnMap(): boolean {
    const markersMissingOnMap = !this.positionsVisibleOnMap(this.markers());
    const circlesMissingOnMap = !this.positionsVisibleOnMap(this.circles);
    const centerMissingOnMap = this.markers()?.length === 0 && this.circles?.length === 0 && (!(this.map?.googleMap && this.map.googleMap.getBounds()) || !this.map?.googleMap?.getBounds()?.contains(this.center));
    const elementsMissing = markersMissingOnMap || circlesMissingOnMap || centerMissingOnMap;
    return !elementsMissing;
  }

  private positionsVisibleOnMap(values: HasPosition[]) {
    if (!this.map?.googleMap || !this.map.googleMap.getBounds()) {
      return false;
    }
    return values.length === 0 || values.filter(value => value.shouldFitToBounds() && value.position() && !this.map.googleMap.getBounds().contains(value.position())).length === 0;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.triggerContextMenu$.complete();
  }

  ngOnInit(): void {
    this.showMap = !this.initialLoading;
    this.zoomChangedByComponent$
      .pipe(
        tap(() => this.zoomChangedByComponent = true),
        debounceTime(200),
        takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.zoomChangedByComponent = false;
      });
    this.mapsLoaderService.mapsApiLoaded$.pipe(take(1)).subscribe(x => {
      this.googleMapOptions = {
        streetViewControl: false,
        zoom: this.zoom ?? 15,
        styles: mapStyles,
        gestureHandling: this.gestureHandling,
        disableDoubleClickZoom: true,
        minZoom: this.minZoom,
        maxZoom: this.maxZoom ?? undefined,
        zoomControl: false,
        mapTypeControl: false,
        disableDefaultUI: this.disableDefaultUi,
        fullscreenControl: this.enableFullscreenControl,
        center: this.center,
        cameraControl: false
      };

      combineLatest([this.appendPopupsThrottleSubject$, this.markers$]).pipe(takeUntil(this.ngUnsubscribe), throttleTime(this.appendPopupsDelay)).subscribe(() => this.appendPopups());

      combineLatest([this.onChanges, this.mapsLoaderService.mapsApiLoaded$]).pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(([changes, apiLoaded]) => {
          this.debugLog('on changes', changes);
          if (changes['initialLoading'] && changes['initialLoading'].currentValue === false) {
            this.debugLog('initial loading done, starting fit to bounds');
            this.startFitToBounds();
            this.fitToBounds(!this.fitToBoundsOnMarkerChanges);
          }
          if (changes['center']) {
            this.debugLog('center changed');
            this.googleMapOptions.center = changes['center'].currentValue;
            this.fitToBounds();
          }
          if (changes['zoom']) {
            this.zoomChanges.next(changes['zoom'].currentValue);
          }

          if (changes['modesSetter'] || changes['showSelector']) {
            this.debugLog('modes or showSelector changed');
            this.checkTopLeftControls();
          }
        });

      if (!this.initialLoading) {
        this.startFitToBounds();
        this.showMap = true;
      }

      combineLatest([this.zoomChanges, this.mapsLoaderService.mapsApiLoaded$])
        .pipe(debounceTime(50), takeUntil(this.ngUnsubscribe))
        .subscribe(([zoom, apiloaded]) => {
          this.zoomChangedByComponent$.next();
          const zoomToSet = zoom ?? 15;
          this.debugLog('setting zoom to', zoomToSet);
          this.googleMapOptions.zoom = zoomToSet;
          this.map?.googleMap?.setZoom(zoomToSet);
        });
    });
  }

  private startFitToBounds() {
    combineLatest([this.fitToBoundsThrottleSubject, this.mapsLoaderService.mapsApiLoaded$])
      .pipe(debounceTime(this.fitToBoundsDelay), takeUntil(this.stopFitToBounds), takeUntil(this.ngUnsubscribe))
      .subscribe(([value]) => this.fitToBoundsOnGoogleMap(value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.debugLog('on changes component', changes);
    this.onChanges.next(changes);
  }

  ngAfterContentInit(): void {
    this.mapsLoaderService.mapsApiLoaded$.pipe(take(1)).subscribe(() => {
      this.circles = this.circleQueryList.toArray();
      this.infoWindows = this.infoWindowsQueryList.toArray();

      this.markerQueryList.changes.pipe(
        takeUntil(this.ngUnsubscribe),
        startWith(this.markerQueryList.toArray())
      ).subscribe(value => {
        if (this.closeAllWindowsOnMarkerChange) {
          this.mapMarkerInfoWindowService.closeAllInfoWindows();
        }
        this.markers.set(value);
      });
      this.circleQueryList.changes.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
        this.circles = value;
        this.fitToBounds();
      });

      this.infoWindowsQueryList.changes.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
        this.infoWindows = value;
      });
    });

    this.triggerContextMenu$.pipe(
      debounceTime(80),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(event => {
      this.openContextMenu(event.event, event.anchor, event.target);
    });
  }

  ngAfterViewInit(): void {
    merge(this.divsToShowPopups.changes, this.markerQueryList.changes, this.markerLabelsToShow.changes)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(divsToShow => {
        this.appendPopups();
      });

    this.markerLabelsToShow.changes.subscribe(() => this.appendPopups());

    this.googleMapMarkers.changes.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(value => {
      this.debugLog('google map markers changed', value, this.markers());
      this.fitToBounds(!this.fitToBoundsOnMarkerChanges);
      this.appendPopups();
    });

    this.mapsLoaderService.mapsApiLoaded$.pipe(take(1)).subscribe(() => {
      this.debugLog('fitting bounds after view init', this.markers(), this.markerQueryList);
      this.appendPopups();
      this.fitToBounds(false);
    });

    if (!this._activeMapType()) {
      const storedMapType = getMapLocalStorage(this._localStorageKey())?.activeMapType;
      if (storedMapType) {
        this.activeMapType = storedMapType;
      }
    }

    this.mapTypeChanged.subscribe(eventType => {
      if (this._localStorageKey()) {
        saveMapLocalStorage(this._localStorageKey(), { activeMapType: eventType });
      }
    });
  }

  private labelHasMarker(marker: MapMarkerComponent) {
    return this.markers().find(m => m.options().position.lat === marker.options().position.lat && m.options().position.lng === marker.options().position.lng);
  }

  private equalPosition(a: google.maps.LatLng | google.maps.LatLngLiteral, b: google.maps.LatLng | google.maps.LatLngLiteral) {
    return a.lat === b.lat && a.lng === b.lng;
  }

  private appendPopups() {
    this.mapsLoaderService.mapsApiLoaded$.pipe(take(1)).subscribe(() => {
      if (!this.divsToShowPopups || !this.infoWindowsQueryList || !this.markerLabelsToShow) {
        return;
      }
      const divsToShowArray = this.divsToShowPopups.toArray();
      this.removePopupsWithChangedPosition();
      this.clearExistingPopups();
      for (let i = 0; i < divsToShowArray.length; i++) {
        const divsToShowArrayElement = divsToShowArray[i];
        const mapInfo = this.infoWindowsQueryList.toArray()[i];
        const position = mapInfo.options.position as google.maps.LatLng;
        if (!position) {
          continue;
        }
        if (this.customPopups.find(value => value.reference === mapInfo)) {
          continue;
        }
        const customPopup = new CustomPopup(
          position ? position : new google.maps.LatLng(42, 42),
          divsToShowArrayElement.nativeElement,
        );
        customPopup.overlayView.setMap(this.map.googleMap);
        this.customPopups.push({ popup: customPopup, reference: mapInfo });
      }
      const mapBounds = this.map?.getBounds();
      const markerLabelsToShow = this.markerLabelsToShow.toArray();
      for (let i = 0; i < markerLabelsToShow.length; i++) {
        const markerLabelDirective = markerLabelsToShow[i];
        const qpointMarker = markerLabelDirective.markerRef;
        if (!qpointMarker.labelTemplateRef) {
          continue;
        }
        const position = qpointMarker.options().position as google.maps.LatLng;
        if (!position) {
          continue;
        }new google.maps.LatLngBounds()
        const existingPopup = this.customPopups.find(value => value.reference === qpointMarker);
        const currentZoom = this.map?.googleMap?.getZoom();
        if (existingPopup) {
          if (currentZoom < this.minZoomToShowLabels || !mapBounds?.contains(qpointMarker.position())) {
            existingPopup.popup.overlayView.setMap(null);
            this.customPopups.splice(this.customPopups.indexOf(existingPopup), 1);
          }
          continue;
        }
        if (currentZoom >= this.minZoomToShowLabels && mapBounds?.contains(qpointMarker.position())) {
          const customPopup = new CustomPopup(
            position ? position : new google.maps.LatLng(42, 42),
            markerLabelDirective.elementRef.nativeElement,
            CustomPopupPosition.right,
            qpointMarker.labelTemplateDirective
          );
          customPopup.overlayView.setMap(this.map.googleMap);
          this.customPopups.push({ popup: customPopup, reference: qpointMarker });
          this.cdr.markForCheck();
        }
      }
    });
    this.clearOrphandDOMPopups(orphandElement => {
      const googleMapContainer = document.querySelector('google-map');
      return orphandElement?.parentElement && googleMapContainer?.contains(orphandElement);
    });
  }

  private clearOrphandDOMPopups(condition: (element: HTMLElement) => boolean) {
    const customPopups = document.querySelectorAll<HTMLElement>('div[qpointMarkerLabelInternal]');
    customPopups.forEach(customPopup => {
      if (condition(customPopup)) {
        this.renderer.removeChild(customPopup.parentElement, customPopup);
      }
    });
  }


  private clearExistingPopups() {
    for (const customPopup of this.customPopups) {
      customPopup.popup.overlayView.onRemove();
      customPopup.popup.overlayView.setMap(null);
    }
    this.cdr.markForCheck();
    this.customPopups = [];
  }

  private clearMarkerLabel(marker: MapMarkerComponent) {
    const popupsToClear = this.customPopups.filter(p => this.equalPosition(p.reference.position(), marker.position()));
    this.customPopups = this.customPopups.filter(p => !this.equalPosition(p.reference.position(), marker.position()));
    for (const customPopup of popupsToClear) {
      customPopup.popup.overlayView.onRemove();
      customPopup.popup.overlayView.setMap(null);
    }
    this.cdr.markForCheck();
  }


  public fitToBounds(checkVisibilityOfMarkers = true) {
    this.fitToBoundsThrottleSubject.next(checkVisibilityOfMarkers);
  }

  public manualFitToBounds(checkVisibilityOfMarkers = true, setZoomOnActualLevel = true) {
    this.fitToBoundsOnGoogleMap(checkVisibilityOfMarkers, setZoomOnActualLevel, false, true);
  }

  public manualFitToBoundsOfPois(pois: LatLngLiteral[], checkVisibilityOfMarkers = true, setZoomOnActualLevel = true) {
    this.fitToBoundsOnGoogleMap(checkVisibilityOfMarkers, setZoomOnActualLevel, false, true, pois);
  }

  public setZoom(zoom: number) {
    this.map.googleMap.setZoom(zoom);
  }

  private fitToBoundsOnGoogleMap(checkVisibilityOfMarkers = true, setZoomOnActualLevel = true, checkBoundsChanged = true, ignoreStickToCenter = false, boundsToConsider?: LatLngLiteral[]) {
    const pointsForFitBounds: (LatLng | LatLngLiteral)[] = boundsToConsider ?? [];
    const stickToCenter = this.stickToCenter && !ignoreStickToCenter;
    this.debugLog('fitToBoundsOnGoogleMap');
    if (checkVisibilityOfMarkers && this.allElementsVisibleOnMap() && !stickToCenter) {
      this.debugLog('ignoring fit to bounds because elements visible', this.markers(), this.circles);
      return;
    }

    if (pointsForFitBounds.length === 0) {
      const mapMarkers = this.googleMapMarkers.toArray();
      for (let i = 0; i < mapMarkers.length; i++) {
        const marker = mapMarkers[i];
        this.debugLog('considering marker', marker);
        if (marker.getPosition() && this.markerQueryList.get(i).shouldFitToBounds()) {
          this.debugLog('append bounds marker', marker);
          pointsForFitBounds.push(marker.getPosition());
        }
      }

      this.circles.forEach((circle, index) => {
        if (circle.options?.center && this.circleQueryList.get(index).shouldFitToBounds()) {
          pointsForFitBounds.push(circle.options.center);
        }
      });

      const pointsForFitBoundsIsCenter = pointsForFitBounds.length === 0 && this.center;
      if (pointsForFitBoundsIsCenter) {
        pointsForFitBounds.push(this.center);
      }
    }

    const bounds = new google.maps.LatLngBounds();
    for (const bound of pointsForFitBounds) {
      bounds.extend(bound);
    }
    this.debugLog('map is ready', this.map);
    this.debugLog('fitting bounds based on points', pointsForFitBounds);
    this.debugLog('map is already active', this.map);
    if (this.map) {
      timer(1).subscribe(() => {
        if (stickToCenter) {
          if (!this.center || bounds.getCenter()) {
            this.debugLog('no center available');
          }
          this.map.googleMap.setCenter(this.center ?? bounds.getCenter());
        } else {
          if (bounds.isEmpty()) {
            this.debugLog('no bounds detected');
            this.showMap = true;
            return;
          }

          if (!checkBoundsChanged || !isEqual(this.previousBounds, bounds)) {
            this.zoomChangedByComponent$.next();
            this.debugLog('fit to bounds', bounds.toJSON());
            this.map.fitBounds(bounds);
            this.previousBounds = bounds;
          }
        }

        this.showMap = true;
      });

      this.debugLog('zoom after fitToBound',
        'setZoomOnActualLevel: ' + setZoomOnActualLevel,
        'setZoomLevelOnFitBounds: ' + this.setZoomLevelOnFitBounds);
      if (setZoomOnActualLevel) {
        const isSinglePoi = pointsForFitBounds.length === 1;
        this.mapIdleTriggeredAction.pipe(take(1), takeUntil(this.ngUnsubscribe))
          .subscribe({
            next: () => {
              if ((this.setZoomLevelOnFitBounds || ignoreStickToCenter) && isSinglePoi) {
                this.debugLog('zoom changes', this.zoom, isSinglePoi);
                this.zoomChanges.next(this.zoom);
              }
            },
            complete: () => {
              this.debugLog('fit to bounds completed');
            }
          });
      }
    } else {
      this.showMap = true;
    }
  }

  mapIdle($event: void) {
    if (!this.mapInitialLoaded) {
      this.mapInitialLoaded = true;
      this.mapLoaded.next(this.map.googleMap);
    }
    this.mapIdleTriggeredAction.next();

  }

  markerDragEnd($event: google.maps.MapMouseEvent, marker: MapMarkerComponent) {
    marker.mapDragend.next($event);
  }

  markerDragStart($event: google.maps.MapMouseEvent, marker: MapMarkerComponent) {
    marker.mapDragStart.next($event);
  }

  markerMouseover($event: google.maps.MapMouseEvent, marker: MapMarkerComponent) {
    marker.mapMouseover.next($event);
  }

  markerMouseout($event: google.maps.MapMouseEvent, marker: MapMarkerComponent) {
    marker.mapMouseout.next($event);
  }


  markerMouseDown($event: google.maps.MapMouseEvent, marker: MapMarkerComponent) {
    this.longClickTimer = timer(500).subscribe(() => marker.mapLongClick.emit($event))
  }

  markerMouseUp() {
    if(this.longClickTimer) {
      this.longClickTimer.unsubscribe();
      this.longClickTimer = null;
    }
  }

  public googleMapMapCenterChanged() {
    this.appendPopupsThrottleSubject$.next();
    this.mapCenterChanged.next();
  }

  private removePopupsWithChangedPosition() {
    const popupsToKeep = [];
    for (const customPopup of this.customPopups) {
      const isLngDifferent = customPopup.popup.position()?.lng !== customPopup.reference.position().lng;
      const isLatDifferent = customPopup.popup.position()?.lat !== customPopup.reference.position().lat;
      if (isLngDifferent || isLatDifferent) {
        customPopup.popup.overlayView.setMap(null);
      } else {
        popupsToKeep.push(customPopup);
      }
    }
    this.customPopups = [...popupsToKeep];
  }

  mapDragend($event: void) {
    if (this.stopFitToBoundsOnManualInteraction) {
      this.debugLog('stop fit to bounds');
      this.stopFitToBounds.next();
    }
  }

  private debugLog(...message: any) {
    if (!this.debugOutput) {
      return;
    }
    console.log([...message]);
  }

  onZoomChanged($event: void) {
    this.zoomChanged.emit(this.map.googleMap.getZoom());
    if (this.stopFitToBoundsOnManualInteraction && !this.zoomChangedByComponent) {
      this.debugLog('stop fit to bounds');
      this.stopFitToBounds.next();
    }
    this.appendPopups();
  }

  public openInfoWindow(marker: MapMarkerComponent, closeOthers = true): void {
    marker.openInfoWindow(closeOthers);
  }

  public closeInfoWindow(marker: MapMarkerComponent) {
    marker.closeInfoWindow();
  }

  public closeAllInfoWindows() {
    this.googleInfoWindows.toArray().forEach(infoWindow => infoWindow.close());
    this.mapMarkerInfoWindowService.closeAllInfoWindows();
  }

  public onMapClicked($event: google.maps.MapMouseEvent): void {
    this.closeAllInfoWindows();
    this.checkAndClosePopupRef();
    this.mapClick.next($event);
  }

  public onMapRightClick($event: google.maps.MapMouseEvent): void {
    this.mapRightClick.next($event);
    this.resetLastRightClickTarget();
    this.triggerContextMenu$.next({ event: $event.domEvent as MouseEvent });
  }

  setActiveMapPolygonComponent(component: MapPolygonComponent, event: google.maps.PolyMouseEvent, index: number) {
    this.mapRightClick.emit(event);
    this.debugLog('set active map polygon');
    if (!component.canDeleteVertex(index) && event.vertex !== undefined) {
      return;
    }
    this.resetActivePolyline();

    this.mapPolygonStateService.setActivePolygonComponent(component, event, index);
    this.lastRightclickTarget = MapComponent.detectTargetComponent(event, TargetComponent.Polygon);
    this.triggerContextMenu$.next(
      {
        event: event.domEvent as MouseEvent,
        anchor: event.vertex != null || event.edge != null ? event.domEvent.target as HTMLElement : undefined,
        target: 'Polygon',
      });
  }


  setActiveMapPolylineComponent(polyline: MapPolylineComponent, event: google.maps.PolyMouseEvent) {
    this.mapRightClick.emit(event);
    this.debugLog('set active map polyline');
    if (!polyline.canDeleteVertex && !polyline.manualMapModeActive) {
      return;
    }
    this.resetActivePolygon();

    this.mapPolylineStateService.setActivePolyline(polyline, event);
    this.lastRightclickTarget = MapComponent.detectTargetComponent(event, TargetComponent.Polyline);
    this.triggerContextMenu$.next(
      {
        event: event.domEvent as MouseEvent,
        anchor: event.vertex != null || event.edge != null ? event.domEvent.target as HTMLElement : undefined,
        target: 'Polyline'
      });
  }

  private static detectTargetComponent(event: google.maps.PolyMouseEvent, defaultTargetComponent: TargetComponent): TargetComponent {
    if (event.vertex) {
      return TargetComponent.Vertex;
    }
    if (event.edge) {
      return TargetComponent.Edge;
    }
    return defaultTargetComponent;
  }

  deletePolygonVertex() {
    this.mapPolygonStateService.deletePolygonVertex();
    this.resetActivePolygon();
    this.checkAndClosePopupRef();
  }

  deletePolylineVertex() {
    this.mapPolylineStateService.deletePolylineVertex();
    this.resetActivePolyline();
    this.checkAndClosePopupRef();
  }

  resetPolyline() {
    this.mapPolylineStateService.resetPolyline();
    this.resetActivePolyline();
    this.checkAndClosePopupRef();
  }

  resetActivePolygon() {
    this.mapPolygonStateService.setActivePolygonComponent(null, null, null);
  }

  resetActivePolyline() {
    this.mapPolylineStateService.setActivePolyline(null, null);
  }

  openContextMenu($event: MouseEvent, anchor?: HTMLElement | ElementRef, target?: ContextMenuTarget) {
    this.checkAndClosePopupRef();

    if (this.contextMenuDisabled ? this.contextMenuDisabled() : false) {
      this.resetLastRightClickTarget();
      return;
    }

    const visibleContextMenuItems = this.contextMenu
      .filter(i => i.targetComponents.includes(this.lastRightclickTarget) &&
        ((i?.modes || [])?.some(mode => this.mapModeState.isModeActive(mode)) || (i.modes || [])?.length === 0)
      );
    if (visibleContextMenuItems.length === 0) {
      this.resetLastRightClickTarget();
      return;
    }

    visibleContextMenuItems.filter(i => i.internalFunctionType === FunctionType.DeleteVertex)
      .forEach(i => {
        switch (target) {
          case 'Polygon':
            return i.entryClick = this.deletePolygonVertex.bind(this);
          case 'Polyline':
            return i.entryClick = this.deletePolylineVertex.bind(this);
        }
      });

    visibleContextMenuItems.filter(i => i.internalFunctionType === FunctionType.ResetPolyline)
      .forEach(i => {
        switch (target) {
          case 'Polyline':
            return i.entryClick = this.resetPolyline.bind(this);
        }
      });

    const isFullscreen = this.isFullscreen;

    this.popupRef = this.popupService.open({
      content: MapContextMenuComponent,
      offset: {
        top: isFullscreen ? $event.y : anchor ? 0 : $event.pageY,
        left: isFullscreen ? $event.x : anchor ? 0 : $event.pageX,
      },
      positionMode: 'absolute',
      anchor,
      collision: { horizontal: 'flip', vertical: 'fit' }
    });

    if (isFullscreen) {
      this.map.googleMap.getDiv().firstChild.appendChild(this.popupRef.popupElement);
    }

    const instance: MapContextMenuComponent = this.popupRef.content.instance;
    instance.contextMenu = visibleContextMenuItems;
    instance.event = $event;
    instance.geoCoordinate = this.toLatLng($event);
    instance.popupRef = this.popupRef;

    if (this.lastRightclickTarget === TargetComponent.Vertex || this.lastRightclickTarget === TargetComponent.Polygon || this.lastRightclickTarget === TargetComponent.Polyline) {
      switch (target) {
        case 'Polyline':
          return instance.data = this.mapPolylineStateService.activePolylineComponent.polyline;
        case 'Polygon':
          return instance.data = this.mapPolygonStateService.activePolygonComponent.polygon;
      }
    }

    instance.popupRef = this.popupRef;
    this.resetLastRightClickTarget();
  }

  checkAndClosePopupRef() {
    if (this.popupRef) {
      this.popupRef.close();
    }
    if (this.mapTypeControlRef?.mapTypeSelectionPopupVisible()) {
      this.mapTypeControlRef.toggleMapTypeSelectionPopup();
    }
    if (this.zoomControlsRef?.fitBoundsSelectionPopupVisible()) {
      this.zoomControlsRef.toggleFitBoundsSelectionPopup();
    }
    if (this.zoomControlsRef?.focusSelectionPopupVisible()) {
      this.zoomControlsRef.toggleFocusSelectionPopup();
    }
  }

  public checkPointWithinBounds(point: ILatLng): boolean {
    const projection = this.map.getProjection();
    const topRight = projection.fromLatLngToPoint(this.map.getBounds().getNorthEast());
    const bottomLeft = projection.fromLatLngToPoint(this.map.getBounds().getSouthWest());
    const geoPoint = projection.fromLatLngToPoint(new google.maps.LatLng(point.lat, point.lng));
    if (geoPoint.x > bottomLeft.x && geoPoint.x < topRight.x && geoPoint.y > topRight.y && geoPoint.y < bottomLeft.y) {
      return true;
    }
    return false;
  }

  private toLatLng($event: MouseEvent): LatLng {
    const bound = ($event.target as HTMLElement).getBoundingClientRect();
    const x = $event.clientX - bound.left;
    const y = $event.clientY - bound.top;

    const projection = this.map.getProjection();
    const topRight = projection.fromLatLngToPoint(this.map.getBounds().getNorthEast());
    const bottomLeft = projection.fromLatLngToPoint(this.map.getBounds().getSouthWest());
    const scale = 1 << this.map.getZoom();

    return projection.fromPointToLatLng(new google.maps.Point(x / scale + bottomLeft.x, y / scale + topRight.y));
  }

  private setupPolygonEditListener(polygons: MapPolygon[]) {
    this.polygonListener.forEach(listener => listener.remove());

    polygons.forEach((polygon, index) => {
      const setAt = google.maps.event.addListener(polygon.getPath(), 'set_at', () => {
        this.mapPolygonStateService.updatePolygon(polygon, this.polygons()[index], index);
      });

      const insertAt = google.maps.event.addListener(polygon.getPath(), 'insert_at', () => {
        this.mapPolygonStateService.updatePolygon(polygon, this.polygons()[index], index);
      });

      this.polygonListener.push(setAt, insertAt);
    });
  }

  private setupPolylineEditListener(polylines: MapPolyline[]) {
    this.polylineListener.forEach(listener => listener.remove());

    polylines.forEach((polyline, index) => {
      const setAt = google.maps.event.addListener(polyline.getPath(), 'set_at', event => {
        this.mapPolylineStateService.updatePolyline(polyline, this.polylines()[index]);
        this.cdr.markForCheck();
      });

      const insertAt = google.maps.event.addListener(polyline.getPath(), 'insert_at', () => {
        this.mapPolylineStateService.updatePolyline(polyline, this.polylines()[index]);
        this.cdr.markForCheck();
      });

      this.polylineListener.push(setAt, insertAt);
    });
  }

  polylineTrackBy(index, item: MapPolylineComponent) {
    return item.polyline?.coordinates;
  }

  directionTrackBy(index, item: MapDirectionComponent) {
    return item.direction()?.request;
  }

  private checkTopLeftControls(map?: google.maps.Map) {
    const gmap = map ?? this.map;
    this.debugLog('checkModeControls: ', this.map);
    if (!gmap) {
      return;
    }

    gmap.controls[google.maps.ControlPosition.TOP_LEFT].clear();

    if (this.showSelector) {
      this.debugLog('show selector');
      const selectorDiv = document.createElement('div');
      selectorDiv.style.margin = '0px';
      selectorDiv.style.userSelect = 'none';

      const customInjector = Injector.create({
        providers: [{ provide: MapModeStateService, useValue: this.mapModeState }],
        parent: this.injector
      });

      const embeddedView = this.viewContainerRef.createEmbeddedView(this.selectorTemplate, {}, { injector: customInjector });
      selectorDiv.appendChild(embeddedView.rootNodes[0]);
      gmap.controls[google.maps.ControlPosition.TOP_LEFT].push(selectorDiv);
    }

    if (!this.showSelector && this.mapModeState.modes && this.mapModeState.modes.length > 0) {
      this.debugLog('show modes', this.mapModeState.modes);
      const wrapper = document.createElement('div');
      wrapper.style.margin = '10px'; // default google maps margin
      wrapper.style.userSelect = 'none';
      const componentRef = this.viewContainerRef.createComponent(MapModeSelectionComponent, {
        injector: this.injector,
      });
      wrapper.appendChild(componentRef.location.nativeElement);
      gmap.controls[google.maps.ControlPosition.TOP_LEFT].push(wrapper);
    }
  }

  /**
   * can be used to append dialogs that should be available in fullscreen
   */
  getMapDialogAnchor(): HTMLElement {
    if (!this.map) {
      return undefined;
    }
    this.map?.controls[google.maps.ControlPosition.TOP_CENTER].clear();
    const div = document.createElement('div');
    this.map?.controls[google.maps.ControlPosition.TOP_CENTER].push(div);

    return div;
  }

  resetLastRightClickTarget() {
    this.lastRightclickTarget = TargetComponent.Map;
  }

  createMapZoomComponent() {
    const component: ComponentRef<MapZoomFitToBoundsControlsComponent> =
      this.viewContainerRef.createComponent(
        MapZoomFitToBoundsControlsComponent,
        {
          injector: this.injector,
          index: null
        }
      );
    component.instance.mapComponent = this;
    component.instance.minZoom = this.minZoom ?? 0;
    component.instance.maxZoom = this.maxZoom ?? 22;
    component.instance.showZoomButtons = this.showZoomButtons;
    component.instance.zoomInLabel = this.zoomInLabelPath;
    component.instance.zoomOutLabel = this.zoomOutLabelPath;

    component.instance.showFitBoundsButton = this.showFitBoundsButton;
    component.instance.fitBoundsLabel = this.fitBoundsLabelPath;
    component.instance.overviewLabel = this.overviewPath;
    component.instance.locationLabel = this.locationPath;
    component.instance.activeFitBounds = this._activeFitBounds();
    component.instance.fitBoundsChanged = this.fitBoundsChanged;

    component.instance.showFocusButton = this._showFocusButton();
    component.instance.focusButtonToolTip = this.focusButtonToolTipPath;
    component.instance.focusElements = this._focusElements();
    component.instance.activeFocus = this.activeFocus;
    component.instance.focusChanged = this.focusChanged;

    this.closeZoomTypeSelectionPopup.subscribe(() => component.instance.closeSelectionPopup());
    component.instance.closeOtherPopups.subscribe(() => this.closeMapTypeSelectionPopup.next(true));
    return component;
  }

  createMapTypeControl() {
    const component: ComponentRef<MapTypeControlComponent> =
      this.viewContainerRef.createComponent(
        MapTypeControlComponent,
        {
          injector: this.injector,
          index: null
        }
      );
    component.instance.mapComponent = this;
    component.instance.mapTypeLabel = this.mapTypePath;
    component.instance.roadmapLabel = this.roadmapPath;
    component.instance.hybridLabel = this.hybridPath;
    component.instance.activeMapType = this._activeMapType;
    component.instance.mapTypeChanged = this.mapTypeChanged;
    this.closeMapTypeSelectionPopup.subscribe(() => component.instance.closeMapTypeSelectionPopup());
    component.instance.closeOtherPopups.subscribe(() => this.closeZoomTypeSelectionPopup.next(true));
    return component;
  }

  private createWrapper(styles: { marginBottom?: string, margin?: string } = {}): HTMLDivElement {
    const wrapper = document.createElement('div');
    if (styles.margin) {
      wrapper.style.margin = styles.margin;
    } else {
      wrapper.style.margin = '10px';
    }
    wrapper.style.userSelect = 'none';
    if (styles.marginBottom) {
      wrapper.style.marginBottom = styles.marginBottom;
    }
    return wrapper;
  }

  isZoomAndMapTypeSamePosition() {
    return this.zoomPosition === this.mapTypePosition && this.mapTypePosition === google.maps.ControlPosition.RIGHT_BOTTOM;
  }

  handleMapInitialized(map: google.maps.Map) {
    this.checkTopLeftControls(map);
    if (!this.disableDefaultUi) {
      const zoomControls = this.createMapZoomComponent();
      const mapTypeControl = this.createMapTypeControl();

      this.zoomPosition = this.zoomPosition ?? google.maps.ControlPosition.RIGHT_BOTTOM;
      this.mapTypePosition = this.mapTypePosition ?? google.maps.ControlPosition.TOP_RIGHT;

      const zoomControlsWrapper = this.createWrapper({
        margin: this.isZoomAndMapTypeSamePosition() ? '0px' : null,
      });
      zoomControlsWrapper.appendChild(zoomControls.location.nativeElement);

      const mapTypeControlWrapper = this.createWrapper({
        marginBottom: this.isZoomAndMapTypeSamePosition() ? '0px' : this.enableFullscreenControl ? '-10px' : '10px',
        margin: this.isZoomAndMapTypeSamePosition() ? '0px' : null,
      });
      mapTypeControlWrapper.appendChild(mapTypeControl.location.nativeElement);

      if (this._activeMapType()) {
        map.setMapTypeId(this._activeMapType());
      }

      if (this.isZoomAndMapTypeSamePosition()) {
        const combinedWrapper = this.createWrapper();
        mapTypeControlWrapper.classList.remove('buttons-container');
        zoomControlsWrapper.classList.remove('buttons-container');
        combinedWrapper.classList.add('buttons-container');
        combinedWrapper.appendChild(mapTypeControlWrapper);
        combinedWrapper.appendChild(zoomControlsWrapper);
        map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(combinedWrapper);
      } else {
        map.controls[this.zoomPosition].push(zoomControlsWrapper);
        map.controls[this.mapTypePosition].push(mapTypeControlWrapper);
      }

      this.mapTypeControlRef = mapTypeControl.instance;
      this.zoomControlsRef = zoomControls.instance;
    }
  }

  markerTrackFn(index: number, marker: MapMarkerComponent) {
    return marker?.position();
  }
}
